import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Check, Signal, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const featuresByPlan = {
    lite: ["1 phone number included", "200 SMS / month", "Voice + voicemail", "No ads, ever"],
    plus: ["1 phone number included", "1,000 SMS / month", "Priority voice routing", "Free local calling", "Custom voicemail greeting"],
    pro: ["1 phone number included", "5,000 SMS / month", "International calling", "Concierge support", "API access"],
};

export default function Billing() {
    const { user } = useAuth();
    const [plans, setPlans] = useState([]);
    const [loadingPlan, setLoadingPlan] = useState(null);
    const [cycle, setCycle] = useState("monthly");
    const [allowance, setAllowance] = useState(null);
    const [wirelessSub, setWirelessSub] = useState(null);
    const [mobilePlans, setMobilePlans] = useState([]);
    const [switchingPlan, setSwitchingPlan] = useState(null);
    const navigate = useNavigate();
    const selectedNumber = new URLSearchParams(window.location.search).get("number");
    const trialEligible = false;
    const trialActive = user?.trial_ends_at && new Date(user.trial_ends_at) > new Date();

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/plans");
            setPlans(data);
        })();
        (async () => {
            try {
                const { data } = await api.get("/numbers/allowance");
                setAllowance(data);
            } catch {}
        })();
        (async () => {
            try {
                const { data } = await api.get("/mobile/plans");
                setMobilePlans(data.plans || []);
            } catch {}
        })();
        loadWireless();
    }, []);

    const loadWireless = async () => {
        try {
            const { data } = await api.get("/mobile/subscription");
            setWirelessSub((data.subscriptions || []).find((x) => x.status === "active") || null);
        } catch {}
    };

    const switchWirelessPlan = async (plan_id) => {
        if (!wirelessSub) return;
        setSwitchingPlan(plan_id);
        try {
            await api.post(`/mobile/subscription/${wirelessSub.mobile_sub_id}/change-plan`, { plan_id });
            toast.success("Your wireless plan has been updated");
            await loadWireless();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not change plan");
        }
        setSwitchingPlan(null);
    };

    const buyPlan = async (plan_id) => {
        const billing_cycle = cycle === "yearly" ? "yearly" : "monthly";
        // New customers must pick their phone number first — one combined checkout
        if (!selectedNumber && allowance && !allowance.has_plan && allowance.numbers_owned === 0) {
            toast.info("First, pick your phone number — then pay for plan + number together.");
            navigate(`/numbers?plan=${plan_id}&cycle=${billing_cycle}`);
            return;
        }
        setLoadingPlan(plan_id);
        try {
            const { data } = await api.post("/subscriptions/checkout", {
                plan_id,
                billing_cycle,
                origin_url: window.location.origin,
                ...(selectedNumber ? { phone_number: selectedNumber } : {}),
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not open checkout");
            setLoadingPlan(null);
        }
    };

    return (
        <div className="space-y-8" data-testid="billing-page">
            <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">— Billing</p>
                <h1 className="font-display text-4xl font-bold tracking-tight">Plans &amp; credits</h1>
                <p className="text-zinc-600 mt-2">
                    Manage your <span className="font-medium">wireless plan</span> and your <span className="font-medium">second phone number</span> — secure checkout with Stripe.
                </p>
            </div>

            {/* ===== Wireless plan management ===== */}
            <div data-testid="wireless-billing-section">
                <div className="flex items-center gap-2 mb-3">
                    <Signal className="w-4 h-4 text-emerald-deep" />
                    <h2 className="font-mono text-[11px] uppercase tracking-wider text-emerald-deep">FlexLine Mobile — Wireless</h2>
                </div>
                {wirelessSub ? (
                    <>
                        <div className="border border-emerald-deep/40 rounded-sm bg-[#ECFDF5] p-5 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="wireless-current-plan">
                            <div>
                                <p className="text-sm text-zinc-600">Current wireless plan</p>
                                <p className="font-display text-2xl font-light">{wirelessSub.plan_name} · ${wirelessSub.plan_price}/mo</p>
                                <p className="text-xs text-zinc-500 mt-0.5">{wirelessSub.phone_number} · {wirelessSub.data}</p>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-3 gap-4">
                            {mobilePlans.map((p) => {
                                const current = p.id === wirelessSub.plan_id;
                                return (
                                    <div key={p.id} className={`border rounded-sm p-5 ${current ? "border-emerald-deep bg-[#ECFDF5]" : "border-[#DDD6FE] bg-paper"}`} data-testid={`wireless-plan-card-${p.id}`}>
                                        <div className="flex items-center justify-between">
                                            <h3 className="font-display text-xl">{p.name}</h3>
                                            {current && <span className="font-mono text-[10px] uppercase text-emerald-deep border border-emerald-deep rounded-sm px-2 py-0.5">Current</span>}
                                        </div>
                                        <div className="mt-1"><span className="font-display text-3xl font-light">${p.price}</span><span className="text-zinc-500 text-sm">/mo</span></div>
                                        <p className="text-sm text-emerald-deep font-medium mt-1">{p.data} · {p.speed}</p>
                                        <Button
                                            disabled={current || switchingPlan === p.id}
                                            onClick={() => switchWirelessPlan(p.id)}
                                            data-testid={`wireless-switch-${p.id}`}
                                            className={`mt-4 w-full rounded-sm h-10 ${current ? "bg-zinc-100 text-zinc-400" : "bg-emerald-deep text-white hover:bg-emerald-deep/90"}`}
                                        >
                                            {switchingPlan === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : current ? "Your plan" : (p.price > wirelessSub.plan_price ? "Upgrade" : "Switch")}
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                ) : (
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6 flex flex-wrap items-center justify-between gap-4" data-testid="wireless-no-plan">
                        <div>
                            <p className="font-medium">You don&apos;t have a wireless plan yet</p>
                            <p className="text-sm text-zinc-600 mt-1">Get an eSIM data plan with unlimited talk &amp; text — activate in minutes.</p>
                        </div>
                        <Button onClick={() => navigate("/mobile")} data-testid="wireless-get-plan-btn" className="bg-espresso text-[#FAF5FF] hover:bg-black rounded-sm h-10 px-6 shrink-0">
                            Browse wireless plans
                        </Button>
                    </div>
                )}
            </div>

            {/* ===== Second phone number plans ===== */}
            <div className="flex items-center gap-2 pt-2">
                <h2 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Second phone number plans</h2>
            </div>

            {trialActive && (
                <div className="border border-emerald-deep bg-[#ECFDF5] rounded-sm p-4" data-testid="trial-active-banner">
                    <p className="font-medium text-emerald-deep">🎉 Your free trial is active</p>
                    <p className="text-sm text-zinc-600 mt-1">
                        Enjoy Flex Line free until <span className="font-medium">{new Date(user.trial_ends_at).toLocaleDateString()}</span>. Your card will be charged automatically after that. Cancel anytime before then.
                    </p>
                </div>
            )}

            {trialEligible && (
                <div className="border border-[#4C1D95] bg-[#F5F0FF] rounded-sm p-4" data-testid="trial-eligible-banner">
                    <p className="font-medium text-[#4C1D95]">Start with a 3-day free trial</p>
                    <p className="text-sm text-zinc-600 mt-1">
                        Pick a plan below and get your number today — <span className="font-medium">you won't be charged for 3 days.</span> Cancel anytime before then and pay nothing.
                    </p>
                </div>
            )}

            {user && !user.profile_completed && !user.is_admin && (
                <div className="border border-[#D97706] bg-[#FEF3C7] rounded-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="profile-required-banner">
                    <div>
                        <p className="font-medium text-[#D97706]">Complete your customer profile to subscribe</p>
                        <p className="text-sm text-zinc-600 mt-1">
                            We need your name, address and SSN on file before activating phone service.
                        </p>
                    </div>
                    <Button
                        onClick={() => (window.location.href = "/profile?next=billing")}
                        data-testid="complete-profile-btn"
                        className="bg-[#D97706] text-white hover:bg-[#B45309] rounded-sm h-10 shrink-0"
                    >
                        Complete profile
                    </Button>
                </div>
            )}

            {selectedNumber && (
                <div className="border border-[#C4B5FD] bg-[#F5F0FF] rounded-sm p-5 flex items-center gap-3" data-testid="selected-number-banner">
                    <span className="ticker-dot shrink-0" />
                    <p className="text-sm">
                        Selected number <span className="font-mono font-medium">{selectedNumber}</span> is{" "}
                        <span className="font-medium text-emerald-deep">included with your plan</span> — you only pay the
                        subscription price.
                    </p>
                </div>
            )}

            <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="current-plan-card">
                <div>
                    <p className="font-mono text-[10px] uppercase text-zinc-500">Current plan</p>
                    <div className="font-display text-3xl font-light mt-1 capitalize">{user?.plan}</div>
                </div>
                <div>
                    <p className="font-mono text-[10px] uppercase text-zinc-500">Cycle</p>
                    <div className="font-display text-3xl font-light mt-1 capitalize">
                        {user?.billing_cycle || "—"}
                    </div>
                </div>
                <div>
                    <p className="font-mono text-[10px] uppercase text-zinc-500">SMS credits</p>
                    <div className="font-display text-3xl font-light mt-1">{user?.sms_credits}</div>
                </div>
                <div className="md:text-right">
                    <p className="font-mono text-[10px] uppercase text-zinc-500">Account</p>
                    <div className="font-mono text-sm mt-1">{user?.email}</div>
                </div>
            </div>

            {/* Billing cycle toggle */}
            <div className="flex justify-center">
                <div className="inline-flex items-center border border-[#DDD6FE] rounded-sm bg-paper p-1" data-testid="billing-cycle-toggle">
                    <button
                        data-testid="billing-cycle-monthly"
                        onClick={() => setCycle("monthly")}
                        className={`px-5 py-2 text-sm rounded-sm transition-colors ${
                            cycle === "monthly"
                                ? "bg-espresso text-[#FAF5FF]"
                                : "text-zinc-700 hover:bg-cream"
                        }`}
                    >
                        Monthly
                    </button>
                    <button
                        data-testid="billing-cycle-yearly"
                        onClick={() => setCycle("yearly")}
                        className={`px-5 py-2 text-sm rounded-sm transition-colors flex items-center gap-2 ${
                            cycle === "yearly"
                                ? "bg-espresso text-[#FAF5FF]"
                                : "text-zinc-700 hover:bg-cream"
                        }`}
                    >
                        Annual
                        <span className={`font-mono text-[9px] uppercase px-1.5 py-0.5 rounded-sm border ${
                            cycle === "yearly"
                                ? "text-[#C4B5FD] border-[#C4B5FD]"
                                : "text-emerald-deep border-emerald-deep"
                        }`}>
                            Save 20%
                        </span>
                    </button>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-px bg-[#DDD6FE] border border-[#DDD6FE]">
                {plans.map((p) => {
                    const highlight = p.plan_id === "plus";
                    const monthly = p.monthly_price ?? p.price;
                    const yearly = p.yearly_price ?? monthly * 12 * 0.8;
                    const isYearly = cycle === "yearly";
                    const price = isYearly ? yearly : monthly;
                    const period = isYearly ? "year" : "month";
                    const monthlyEquivalent = (yearly / 12).toFixed(2);
                    const fullYearMonthly = (monthly * 12).toFixed(2);
                    return (
                        <div
                            key={p.plan_id}
                            className={`p-10 ${highlight ? "bg-espresso text-[#FAF5FF]" : "bg-paper"}`}
                            data-testid={`plan-card-${p.plan_id}`}
                        >
                            <div className="flex items-center justify-between">
                                <p className={`font-mono text-[11px] uppercase ${highlight ? "text-[#C4B5FD]" : "text-zinc-500"}`}>
                                    — {p.name}
                                </p>
                                {highlight && (
                                    <span className="font-mono text-[10px] uppercase text-[#C4B5FD] border border-[#C4B5FD] px-2 py-0.5 rounded-sm">
                                        Most popular
                                    </span>
                                )}
                            </div>
                            <div className="flex items-end gap-1 mt-4">
                                <span className="font-display text-5xl font-light tracking-tight">${Number(price).toFixed(2)}</span>
                                <span className={`text-sm pb-2 ${highlight ? "text-zinc-400" : "text-zinc-500"}`}>/ {period}</span>
                            </div>
                            {isYearly && (
                                <div className="mt-2 flex items-center gap-2" data-testid={`annual-detail-${p.plan_id}`}>
                                    <span className={`font-mono text-xs ${highlight ? "text-zinc-400" : "text-zinc-500"}`}>
                                        ${monthlyEquivalent}/mo equivalent
                                    </span>
                                    <span className={`font-mono text-[10px] line-through ${highlight ? "text-zinc-500" : "text-zinc-400"}`}>
                                        ${fullYearMonthly}
                                    </span>
                                </div>
                            )}
                            <ul className="mt-8 space-y-3">
                                {(featuresByPlan[p.plan_id] || []).map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-sm">
                                        <Check className={`w-4 h-4 mt-0.5 ${highlight ? "text-[#C4B5FD]" : "text-emerald-deep"}`} />
                                        <span>{f}</span>
                                    </li>
                                ))}
                                {isYearly && (
                                    <li className="flex items-start gap-2 text-sm">
                                        <Check className={`w-4 h-4 mt-0.5 ${highlight ? "text-[#C4B5FD]" : "text-emerald-deep"}`} />
                                        <span className="font-medium">12× credits granted up front</span>
                                    </li>
                                )}
                            </ul>
                            <Button
                                onClick={() => buyPlan(p.plan_id)}
                                disabled={loadingPlan === p.plan_id}
                                data-testid={`buy-plan-${p.plan_id}`}
                                className={`mt-10 w-full rounded-sm h-11 ${
                                    highlight
                                        ? "bg-[#FAF5FF] text-espresso hover:bg-white"
                                        : "bg-espresso text-[#FAF5FF] hover:bg-black"
                                }`}
                            >
                                {loadingPlan === p.plan_id ? "Opening Stripe…" : trialEligible ? "Start 3-day free trial" : `Choose ${p.name}`}
                            </Button>
                            {trialEligible && (
                                <p className={`mt-3 text-xs text-center ${highlight ? "text-zinc-400" : "text-zinc-500"}`} data-testid={`trial-note-${p.plan_id}`}>
                                    Free for 3 days, then ${Number(price).toFixed(2)}/{period}. Cancel anytime.
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
